import { FC, useState, useRef, useEffect, useMemo } from 'react';
import {
  Box,
  Paper,
  Typography,
  TextField,
  IconButton,
  List,
  ListItem,
  Avatar,
  CircularProgress,
  Chip,
} from '@mui/material';
import { Send } from '@mui/icons-material';
import { format } from 'date-fns';
import {
  Concern,
  Message,
  ConcernThread,
  PaginatedMessagesResponse,
} from '../../../../../../apis/messaging/messaging.interfaces';
import { useTranslation } from 'src/hooks/useTranslationHook';
import { MessagingAPI } from '../../../../../../apis/messaging/messaging.api';
import { logger } from '../../../../../../utils/common';
import isAppWithMock from 'src/utils/isAppWithMock';

interface ConcernThreadViewProps {
  concern: Concern | null;
  threadData: ConcernThread | PaginatedMessagesResponse | null;
  isLoading?: boolean;
  onReplySent?: () => void;
  isStaff?: boolean;
}

const isConcernThread = (
  d: ConcernThread | PaginatedMessagesResponse
): d is ConcernThread => 'concern' in d && 'messages' in d;

const ConcernThreadView: FC<ConcernThreadViewProps> = ({
  concern,
  threadData,
  isLoading = false,
  onReplySent,
  isStaff = false,
}) => {
  const { translateText } = useTranslation();
  const [replyText, setReplyText] = useState('');
  const [sending, setSending] = useState(false);
  const messagesScrollContainerRef = useRef<HTMLUListElement>(null);

  const messages: Message[] = useMemo(() => threadData
    ? isConcernThread(threadData)
      ? threadData.messages
      : threadData.messages || []
    : [], [threadData]);

  const displayMessages = useMemo(() => concern
    ? [
      {
        ...concern,
        content: concern.concern_metadata?.description || concern.content,
        isConcern: true,
      },
      ...messages,
    ]
    : [], [concern, messages]);

  useEffect(() => {
    const el = messagesScrollContainerRef.current;
    if (!el) return;
    const id = requestAnimationFrame(() => {
      el.scrollTo({ top: el.scrollHeight, behavior: 'smooth' });
    });
    return () => cancelAnimationFrame(id);
  }, [displayMessages]);

  const handleSendReply = async () => {
    if (!replyText.trim() || !concern) return;
    setSending(true);
    try {
      const withMock = isAppWithMock();
      if (!withMock) {
        await MessagingAPI.sendMessage({
          receiver_id: concern.sender_id,
          content: replyText.trim(),
          institute_ref: concern.institute_ref || 'inst1',
        });
      } else {
        logger.show_success('Reply sent (mock)');
      }
      setReplyText('');
      onReplySent?.();
    } catch (e) {
      logger.show_error('Failed to send reply');
    } finally {
      setSending(false);
    }
  };

  if (!concern) {
    return (
      <Box
        sx={{
          flex: 1,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          p: 3,
        }}
        data-testid="concern-thread-empty"
      >
        <Typography color="text.secondary">
          {translateText('communications.concerns.selectConcern')}
        </Typography>
      </Box>
    );
  }

  const meta = concern.concern_metadata;

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        overflow: 'hidden',
      }}
      data-testid="concern-thread-container"
    >
      <Box
        sx={{
          p: 2,
          borderBottom: 1,
          borderColor: 'divider',
        }}
      >
        <Typography variant="h6">{meta?.subject || concern.content}</Typography>
        <Box sx={{ display: 'flex', gap: 1, mt: 1, flexWrap: 'wrap' }}>
          <Chip
            size="small"
            label={translateText(
              `communications.concerns.categories.${meta?.category || 'other'}`
            )}
          />
          <Chip
            size="small"
            color={
              meta?.priority === 'high'
                ? 'error'
                : meta?.priority === 'medium'
                  ? 'warning'
                  : 'default'
            }
            label={translateText(
              `communications.concerns.priorities.${meta?.priority || 'medium'}`
            )}
          />
        </Box>
      </Box>

      <List
        ref={messagesScrollContainerRef}
        sx={{
          flex: 1,
          overflow: 'auto',
          p: 2,
        }}
      >
        {isLoading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
            <CircularProgress size={32} />
          </Box>
        ) : (
          displayMessages.map((msg, idx) => (
            <ListItem
              key={msg._id || msg.id || idx}
              sx={{
                alignItems: 'flex-start',
                flexDirection: 'column',
                px: 0,
              }}
            >
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'flex-start',
                  gap: 1,
                  width: '100%',
                }}
              >
                <Avatar sx={{ width: 32, height: 32 }}>
                  {(msg as any).sender?.first_name?.[0] || '?'}
                </Avatar>
                <Box sx={{ flex: 1 }}>
                  <Typography variant="caption" color="text.secondary">
                    {(msg as any).sender
                      ? `${(msg as any).sender.first_name} ${(msg as any).sender.last_name}`
                      : 'Unknown'}
                    {' • '}
                    {format(new Date(msg.created_at), 'MMM d, h:mm a')}
                  </Typography>
                  <Typography variant="body2" sx={{ mt: 0.5 }}>
                    {msg.content}
                  </Typography>
                </Box>
              </Box>
            </ListItem>
          ))
        )}
      </List>

      {isStaff && (
        <Paper
          elevation={0}
          sx={{
            p: 2,
            borderTop: 1,
            borderColor: 'divider',
          }}
        >
          <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-end' }}>
            <TextField
              fullWidth
              multiline
              maxRows={3}
              placeholder={translateText('communications.concerns.replyPlaceholder')}
              value={replyText}
              onChange={(e) => setReplyText(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && !e.shiftKey) {
                  e.preventDefault();
                  handleSendReply();
                }
              }}
              size="small"
              data-testid="concern-reply-input"
            />
            <IconButton
              onClick={handleSendReply}
              disabled={!replyText.trim() || sending}
              color="primary"
              data-testid="concern-reply-send-cta"
            >
              {sending ? (
                <CircularProgress size={24} />
              ) : (
                <Send />
              )}
            </IconButton>
          </Box>
        </Paper>
      )}
    </Box>
  );
};

export default ConcernThreadView;
